import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
} from '@reduxjs/toolkit';
import { getTemperatureAndHumidity } from 'scenes/Renolog/services/getTemperatureAndHumidity.service';
import { getMouldRisk } from 'scenes/Renolog/services/getMouldRisk.service';
import { getRenologDevices } from 'scenes/Renolog/services/getRenologDevices.service';

const deviceAdapter = createEntityAdapter({
  selectId: (entry) => entry.deviceId,
});

export const fetchSensorData = createAsyncThunk(
  'renolog/fetchSensorData',
  async ({ projectId, deviceId }, { rejectWithValue }) => {
    try {
      const [temperatureHumidity, mouldRisk] = await Promise.all([
        getTemperatureAndHumidity(projectId, deviceId),
        getMouldRisk(projectId, deviceId),
      ]);
      return {
        deviceId,
        temperatureHumidity,
        mouldRisk,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch sensor data');
    }
  }
);

export const fetchDevices = createAsyncThunk(
  'renolog/fetchDevices',
  async ({ projectId, doSync }, { rejectWithValue }) => {
    try {
      const devices = await getRenologDevices(projectId, doSync);
      return { devices, projectId, doSync };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch devices');
    }
  }
);

const initialState = deviceAdapter.getInitialState({
  loading: {
    devices: false,
    sensorData: {},
  },
  loadingStarted: {
    devices: false,
    sensorData: {},
  },
  error: {
    devices: undefined,
    sensorData: {},
  },
});

const sensorDataSlice = createSlice({
  name: 'renologSensorData',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDevices.pending, (state) => {
        state.loading.devices = true;
        state.loadingStarted.devices = true;
        state.error.devices = undefined;
      })
      .addCase(fetchDevices.fulfilled, (state, action) => {
        const { devices } = action.payload;
        deviceAdapter.setAll(
          state,
          devices.map((d) => ({ ...d }))
        );
        state.loading.devices = false;
        state.error.devices = undefined;
      })
      .addCase(fetchDevices.rejected, (state, action) => {
        state.loading.devices = false;
        state.error.devices = action.payload || 'Unknown error';
      })
      .addCase(fetchSensorData.pending, (state, action) => {
        const { deviceId } = action.meta.arg;
        state.loading.sensorData[deviceId] = true;
        state.loadingStarted.sensorData[deviceId] = true;
        state.error.sensorData[deviceId] = undefined;
      })
      .addCase(fetchSensorData.fulfilled, (state, action) => {
        const { deviceId, temperatureHumidity, mouldRisk } = action.payload;
        const existing = state.entities[deviceId] || {};
        deviceAdapter.upsertOne(state, {
          ...existing,
          deviceId,
          temperatureHumidity,
          mouldRisk,
        });
        state.loading.sensorData[deviceId] = false;
        state.error.sensorData[deviceId] = undefined;
      })
      .addCase(fetchSensorData.rejected, (state, action) => {
        const { deviceId } = action.meta.arg;
        state.loading.sensorData[deviceId] = false;
        state.error.sensorData[deviceId] = action.payload || 'Unknown error';
      });
  },
});

export const deviceSelectors = deviceAdapter.getSelectors(
  (state) => state.renologSensorData
);

export const selectDeviceLoading = (state) =>
  state.renologSensorData.loading.devices;
export const selectSensorDataLoading = (state, deviceId) =>
  state.renologSensorData.loading.sensorData[deviceId];
export const selectDeviceError = (state) =>
  state.renologSensorData.error.devices;
export const selectSensorDataError = (state, deviceId) =>
  state.renologSensorData.error.sensorData[deviceId];

export default sensorDataSlice.reducer;
